import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Cpu,
  FileText,
  Globe,
  HardDrive,
  Layers,
  Network,
  RefreshCw,
  Server,
  X,
  XCircle,
  Zap,
} from "lucide-react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
} from "recharts";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import {
  adminApi,
  PodServiceEntry,
  SystemCustomDomain,
  SystemEvent,
  SystemNamespace,
  SystemNode,
  SystemPlatformSubdomain,
  SystemPod,
  SystemRouting,
  SystemService,
  SystemSummary,
} from "../../lib/adminApi";

// ── Formatters ────────────────────────────────────────────────────────────────

function fmtMiB(bytes: number) {
  if (bytes < 0) return "--";
  return `${Math.round(bytes / 1048576)} MiB`;
}
function fmtMilliCPU(mc: number) {
  if (mc < 0) return "--";
  return mc >= 1000 ? `${(mc / 1000).toFixed(2)} cores` : `${mc}m`;
}
function fmtPct(pct: number) {
  if (pct < 0) return "--";
  return `${pct.toFixed(1)}%`;
}
function pctBarColor(pct: number) {
  if (pct >= 85) return "bg-destructive";
  if (pct >= 65) return "bg-warning";
  return "bg-success";
}
function pctTextColor(pct: number) {
  if (pct >= 85) return "text-destructive";
  if (pct >= 65) return "text-warning";
  return "text-success";
}

// ── Small reusables ───────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: string }) {
  const s = status?.toLowerCase();
  if (s === "ready" || s === "running" || s === "active")
    return <Badge variant="secondary" className="bg-success-bg text-success-fg border-transparent gap-1 text-xs"><CheckCircle2 className="h-3 w-3" />{status}</Badge>;
  if (s === "pending")
    return <Badge variant="secondary" className="bg-warning-bg text-warning-fg border-transparent gap-1 text-xs"><AlertTriangle className="h-3 w-3" />{status}</Badge>;
  return <Badge variant="secondary" className="bg-danger-bg text-danger-fg border-transparent gap-1 text-xs"><XCircle className="h-3 w-3" />{status}</Badge>;
}

function MiniBar({ value, max, color }: { value: number; max: number; color: string }) {
  const pct = max > 0 ? Math.min((value / max) * 100, 100) : 0;
  return (
    <div className="relative h-1.5 w-16 rounded-full bg-muted overflow-hidden">
      <div className={`absolute left-0 top-0 h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
    </div>
  );
}

function UsageBar({ pct, label, sublabel }: { pct: number; label: string; sublabel?: string }) {
  return (
    <div className="space-y-1.5">
      <div className="flex justify-between items-baseline">
        <span className="text-xs text-muted-foreground">{label}</span>
        <span className={`text-xs font-semibold ${pctTextColor(pct)}`}>{fmtPct(pct)}</span>
      </div>
      <div className="relative h-2 w-full rounded-full bg-muted overflow-hidden">
        <div className={`absolute left-0 top-0 h-full rounded-full transition-all ${pctBarColor(pct)}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      {sublabel && <p className="text-2xs text-muted-foreground">{sublabel}</p>}
    </div>
  );
}

// ── KPI card ──────────────────────────────────────────────────────────────────

function KpiCard({
  label, value, sub, icon: Icon, accent, trend,
}: {
  label: string; value: string | number; sub?: string;
  icon: React.ComponentType<{ className?: string }>; accent?: string; trend?: "good" | "bad" | "neutral";
}) {
  return (
    <Card className="relative overflow-hidden">
      <CardContent className="pt-5 pb-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">{label}</p>
            <p className={`text-display tabular-nums ${accent ?? "text-foreground"}`}>{value}</p>
            {sub && <p className="text-xs text-muted-foreground mt-1.5">{sub}</p>}
          </div>
          <div className={`rounded-xl p-2.5 shrink-0 ${trend === "good" ? "bg-success-bg" : trend === "bad" ? "bg-destructive/10" : "bg-muted"}`}>
            <Icon className={`h-5 w-5 ${trend === "good" ? "text-success" : trend === "bad" ? "text-destructive" : "text-muted-foreground"}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// ── Pod state distribution bar ────────────────────────────────────────────────

function PodStateBar({ running, pending, failed }: { running: number; pending: number; failed: number }) {
  const total = running + pending + failed || 1;
  const segments = [
    { label: "Running", count: running, color: "bg-success" },
    { label: "Pending", count: pending, color: "bg-warning" },
    { label: "Failed",  count: failed,  color: "bg-destructive" },
  ];
  return (
    <div className="space-y-3">
      <div className="flex h-3 w-full rounded-full overflow-hidden gap-0.5">
        {segments.map((s) =>
          s.count > 0 ? (
            <div key={s.label} title={`${s.label}: ${s.count}`}
              className={`${s.color} transition-all`}
              style={{ width: `${(s.count / total) * 100}%` }} />
          ) : null
        )}
        {running + pending + failed === 0 && <div className="bg-muted flex-1 rounded-full" />}
      </div>
      <div className="flex gap-4">
        {segments.map((s) => (
          <div key={s.label} className="flex items-center gap-1.5">
            <span className={`h-2 w-2 rounded-full ${s.color}`} />
            <span className="text-xs text-muted-foreground">{s.label}</span>
            <span className={`text-xs font-semibold ${s.label === "Failed" && s.count > 0 ? "text-destructive" : s.label === "Pending" && s.count > 0 ? "text-warning" : "text-foreground"}`}>{s.count}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Donut chart ───────────────────────────────────────────────────────────────

function UsageDonut({ pct, label, sub }: { pct: number; label: string; sub?: string }) {
  const color = pct >= 85 ? "#ef4444" : pct >= 65 ? "#eab308" : "#22c55e";
  const data = [{ value: pct }, { value: Math.max(0, 100 - pct) }];
  return (
    <div className="flex flex-col items-center gap-1">
      <div className="relative h-20 w-20">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} cx="50%" cy="50%" innerRadius={26} outerRadius={36}
              startAngle={90} endAngle={-270} dataKey="value" strokeWidth={0}>
              <Cell fill={color} />
              <Cell fill="hsl(var(--muted))" />
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-sm font-bold tabular-nums" style={{ color }}>{Math.round(pct)}%</span>
        </div>
      </div>
      <p className="text-xs font-medium text-foreground">{label}</p>
      {sub && <p className="text-2xs text-muted-foreground text-center">{sub}</p>}
    </div>
  );
}

// ── Tabs ──────────────────────────────────────────────────────────────────────

type Tab = "overview" | "pods" | "namespaces" | "services" | "events" | "pod-service-map" | "routing";

const TABS: { id: Tab; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: "overview",        label: "Overview",      icon: Activity },
  { id: "pods",            label: "Pods",          icon: Layers },
  { id: "namespaces",      label: "Namespaces",    icon: Globe },
  { id: "services",        label: "Services",      icon: Network },
  { id: "events",          label: "Events",        icon: AlertTriangle },
  { id: "pod-service-map", label: "Pod → Service", icon: Server },
  { id: "routing",         label: "Routing",       icon: Globe },
];

// ── Log drawer ────────────────────────────────────────────────────────────────

function LogDrawer({ pod, namespace, onClose }: { pod: string; namespace: string; onClose: () => void }) {
  const [logs, setLogs] = useState("");
  const [loading, setLoading] = useState(true);
  const [lines, setLines] = useState(100);

  const fetchLogs = useCallback(async (l: number) => {
    setLoading(true);
    try {
      const res = await adminApi.podLogs(namespace, pod, { lines: l });
      setLogs(res.logs || "(no output)");
    } catch { setLogs("Failed to fetch logs."); }
    finally { setLoading(false); }
  }, [namespace, pod]);

  useEffect(() => { fetchLogs(lines); }, [fetchLogs, lines]);

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-end bg-black/40" onClick={onClose}>
      <div className="relative flex h-full w-full max-w-2xl flex-col bg-card shadow-float dark:ring-1 dark:ring-border"
        onClick={(e) => e.stopPropagation()}>
        <div className="flex h-11 shrink-0 items-center gap-2 border-b px-4">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <span className="text-sm font-medium truncate">{pod}</span>
          <span className="text-xs text-muted-foreground">· {namespace}</span>
          <div className="ml-auto flex items-center gap-2">
            <Select value={String(lines)} onValueChange={(v) => setLines(Number(v))}>
              <SelectTrigger className="h-7 text-xs w-24"><SelectValue /></SelectTrigger>
              <SelectContent>
                {[50, 100, 200, 500].map((n) => (
                  <SelectItem key={n} value={String(n)}>{n} lines</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => fetchLogs(lines)}>
              <RefreshCw className="h-3.5 w-3.5" />
            </Button>
            <Button size="icon" variant="ghost" className="h-7 w-7" onClick={onClose}>
              <X className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto p-4">
          {loading ? (
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <RefreshCw className="h-3.5 w-3.5 animate-spin" />Fetching logs…
            </div>
          ) : (
            <pre className="text-xs font-mono leading-5 whitespace-pre-wrap break-all text-foreground">{logs}</pre>
          )}
        </div>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

type SortKey = "name" | "namespace" | "status" | "restarts" | "cpu" | "mem";

export default function AdminSystemPage() {
  const [tab, setTab] = useState<Tab>("overview");

  const [summary, setSummary] = useState<SystemSummary | null>(null);
  const [nodes, setNodes] = useState<SystemNode[]>([]);
  const [pods, setPods] = useState<SystemPod[]>([]);
  const [namespaces, setNamespaces] = useState<SystemNamespace[]>([]);
  const [services, setServices] = useState<SystemService[]>([]);
  const [events, setEvents] = useState<SystemEvent[]>([]);
  const [podSvcMap, setPodSvcMap] = useState<PodServiceEntry[]>([]);
  const [routing, setRouting] = useState<SystemRouting | null>(null);

  const [nsFilter, setNsFilter] = useState("__all__");
  const [sortKey, setSortKey] = useState<SortKey>("restarts");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const [podSearch, setPodSearch] = useState("");
  const [logPod, setLogPod] = useState<{ name: string; namespace: string } | null>(null);

  const [svcNsFilter, setSvcNsFilter] = useState("__all__");
  const [mapNsFilter, setMapNsFilter] = useState("__all__");
  const [mapSearch, setMapSearch] = useState("");
  const [domainSearch, setDomainSearch] = useState("");

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date | null>(null);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const [sum, nodeRes, podRes, nsRes, svcRes, evRes, mapRes, routingRes] = await Promise.all([
        adminApi.systemSummary(),
        adminApi.systemNodes(),
        adminApi.systemPods(),
        adminApi.systemNamespaces(),
        adminApi.systemServices(),
        adminApi.systemEvents(),
        adminApi.systemPodServiceMap(),
        adminApi.systemRouting(),
      ]);
      setSummary(sum);
      setNodes(nodeRes.nodes ?? []);
      setPods(podRes.pods ?? []);
      setNamespaces(nsRes.namespaces ?? []);
      setServices(svcRes.services ?? []);
      setEvents(evRes.events ?? []);
      setPodSvcMap(mapRes.mapping ?? []);
      setRouting(routingRes);
      setLastRefresh(new Date());
      setError(null);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? "Failed to fetch cluster data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(load, 30_000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [load]);

  const podNamespaces = Array.from(new Set(pods.map((p) => p.namespace))).sort();
  const maxCpu = Math.max(...pods.map((p) => p.cpuUsageMillis), 1);
  const maxMem = Math.max(...pods.map((p) => p.memUsageBytes), 1);

  const filteredPods = pods
    .filter((p) => nsFilter === "__all__" || p.namespace === nsFilter)
    .filter((p) => !podSearch || p.name.toLowerCase().includes(podSearch.toLowerCase()) || p.namespace.toLowerCase().includes(podSearch.toLowerCase()))
    .slice()
    .sort((a, b) => {
      let aV: string | number = 0, bV: string | number = 0;
      if (sortKey === "name")      { aV = a.name;           bV = b.name; }
      if (sortKey === "namespace") { aV = a.namespace;      bV = b.namespace; }
      if (sortKey === "status")    { aV = a.status;         bV = b.status; }
      if (sortKey === "restarts")  { aV = a.restarts;       bV = b.restarts; }
      if (sortKey === "cpu")       { aV = a.cpuUsageMillis; bV = b.cpuUsageMillis; }
      if (sortKey === "mem")       { aV = a.memUsageBytes;  bV = b.memUsageBytes; }
      const cmp = typeof aV === "string" ? aV.localeCompare(bV as string) : (aV as number) - (bV as number);
      return sortDir === "asc" ? cmp : -cmp;
    });

  function toggleSort(key: SortKey) {
    if (sortKey === key) setSortDir((d) => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("desc"); }
  }

  function SortableHead({ col, label }: { col: SortKey; label: string }) {
    const active = sortKey === col;
    return (
      <TableHead className="cursor-pointer select-none whitespace-nowrap" onClick={() => toggleSort(col)}>
        <span className={`flex items-center gap-1 ${active ? "text-foreground" : ""}`}>
          {label}{active ? (sortDir === "asc" ? " ↑" : " ↓") : " ↕"}
        </span>
      </TableHead>
    );
  }

  const svcNamespaces = Array.from(new Set(services.map((s) => s.namespace))).sort();
  const filteredSvcs = services.filter((s) => svcNsFilter === "__all__" || s.namespace === svcNsFilter);
  const mapNamespaces = Array.from(new Set(podSvcMap.map((e) => e.namespace))).sort();
  const filteredMap = podSvcMap
    .filter((e) => mapNsFilter === "__all__" || e.namespace === mapNsFilter)
    .filter((e) => !mapSearch || e.podName.toLowerCase().includes(mapSearch.toLowerCase()));

  if (loading) {
    return (
      <div className="flex h-64 items-center justify-center text-muted-foreground text-sm gap-2">
        <RefreshCw className="h-4 w-4 animate-spin" />Loading cluster data…
      </div>
    );
  }
  if (error) {
    return (
      <div className="flex h-64 flex-col items-center justify-center gap-3">
        <XCircle className="h-8 w-8 text-destructive" />
        <p className="text-sm text-destructive">{error}</p>
        <Button variant="outline" size="sm" onClick={load}>Retry</Button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-h3">System Health</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            k3s cluster
            {lastRefresh && <span className="ml-2 text-2xs">· refreshed {lastRefresh.toLocaleTimeString()}</span>}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={load}>
          <RefreshCw className="h-3.5 w-3.5 mr-1.5" />Refresh
        </Button>
      </div>

      {/* KPI row — always visible */}
      {summary && (
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
          <KpiCard label="Nodes" value={`${summary.nodes.ready}/${summary.nodes.total}`}
            sub={summary.nodes.ready === summary.nodes.total ? "All ready" : `${summary.nodes.total - summary.nodes.ready} not ready`}
            icon={Server} trend={summary.nodes.ready === summary.nodes.total ? "good" : "bad"} />
          <KpiCard label="Pods Running" value={summary.pods.running}
            sub={`${summary.pods.pending} pending · ${summary.pods.failed} failed`}
            icon={Activity} accent={summary.pods.failed > 0 ? "text-destructive" : undefined}
            trend={summary.pods.failed > 0 ? "bad" : "good"} />
          <KpiCard label="CPU Usage" value={fmtPct(summary.cpu.percent)}
            sub={`${fmtMilliCPU(summary.cpu.usageMillis)} of ${fmtMilliCPU(summary.cpu.capacityMillis)}`}
            icon={Cpu} accent={pctTextColor(summary.cpu.percent)}
            trend={summary.cpu.percent >= 85 ? "bad" : summary.cpu.percent >= 65 ? "neutral" : "good"} />
          <KpiCard label="Memory Usage" value={fmtPct(summary.memory.percent)}
            sub={`${fmtMiB(summary.memory.usageBytes)} of ${fmtMiB(summary.memory.capacityBytes)}`}
            icon={HardDrive} accent={pctTextColor(summary.memory.percent)}
            trend={summary.memory.percent >= 85 ? "bad" : summary.memory.percent >= 65 ? "neutral" : "good"} />
        </div>
      )}

      {summary && summary.buildsInProgress > 0 && (
        <div className="flex items-center gap-2 rounded-md border-warning/30 bg-warning-bg/50 px-4 py-3 text-sm">
          <Zap className="h-4 w-4 text-warning shrink-0" />
          <span><span className="font-medium text-warning">{summary.buildsInProgress}</span> build{summary.buildsInProgress !== 1 ? "s" : ""} in progress</span>
        </div>
      )}

      {/* Tab bar */}
      <div className="flex gap-0.5 border-b">
        {TABS.map(({ id, label, icon: Icon }) => (
          <button key={id} onClick={() => setTab(id)}
            className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors border-b-2 -mb-px whitespace-nowrap
              ${tab === id ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`}>
            <Icon className="h-3.5 w-3.5" />{label}
            {id === "events" && events.length > 0 && (
              <span className="ml-1 rounded-full bg-destructive/10 px-1.5 py-0.5 text-2xs text-destructive font-semibold">{events.length}</span>
            )}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW ──────────────────────────────────────────────────────────── */}
      {tab === "overview" && summary && (
        <div className="space-y-5">
          <div className="grid gap-4 lg:grid-cols-3">
            <Card className="lg:col-span-2">
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Pod Distribution</CardTitle>
              </CardHeader>
              <CardContent>
                <PodStateBar running={summary.pods.running} pending={summary.pods.pending} failed={summary.pods.failed} />
                <p className="text-xs text-muted-foreground mt-3">{summary.pods.total} pods total across {namespaces.length} namespaces</p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-sm font-medium text-muted-foreground uppercase tracking-wide">Resource Usage</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex justify-around">
                  <UsageDonut pct={summary.cpu.percent} label="CPU"
                    sub={`${fmtMilliCPU(summary.cpu.usageMillis)} / ${fmtMilliCPU(summary.cpu.capacityMillis)}`} />
                  <UsageDonut pct={summary.memory.percent} label="Memory"
                    sub={`${fmtMiB(summary.memory.usageBytes)} / ${fmtMiB(summary.memory.capacityBytes)}`} />
                </div>
              </CardContent>
            </Card>
          </div>

          <div>
            <h2 className="mb-3 text-2xs font-semibold uppercase tracking-wider text-muted-foreground">Nodes ({nodes.length})</h2>
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {nodes.map((node) => (
                <Card key={node.name}>
                  <CardContent className="pt-4 pb-4 space-y-4">
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="text-sm font-semibold truncate">{node.name}</p>
                        <p className="text-2xs text-muted-foreground mt-0.5">{node.roles} · {node.version}</p>
                      </div>
                      <StatusBadge status={node.status} />
                    </div>
                    <div className="space-y-2.5">
                      <UsageBar pct={node.cpuPercent} label="CPU"
                        sublabel={`${fmtMilliCPU(node.cpuUsageMillis)} / ${fmtMilliCPU(node.cpuCapacityMillis)}`} />
                      <UsageBar pct={node.memPercent} label="Memory"
                        sublabel={`${fmtMiB(node.memUsageBytes)} / ${fmtMiB(node.memCapacityBytes)}`} />
                    </div>
                    <p className="text-2xs text-muted-foreground">{node.os} · up {node.age}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>

          {pods.length > 0 && (
            <div className="grid gap-4 lg:grid-cols-2">
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <Cpu className="h-3.5 w-3.5 text-muted-foreground" />Top CPU consumers
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[...pods].sort((a, b) => b.cpuUsageMillis - a.cpuUsageMillis).slice(0, 5).map((p) => (
                    <div key={`${p.namespace}/${p.name}`} className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between mb-1">
                          <span className="text-xs font-mono truncate max-w-[180px]" title={p.name}>{p.name}</span>
                          <span className="text-xs text-muted-foreground ml-2 shrink-0">{fmtMilliCPU(p.cpuUsageMillis)}</span>
                        </div>
                        <div className="relative h-1.5 w-full rounded-full bg-muted overflow-hidden">
                          <div className={`absolute left-0 top-0 h-full rounded-full ${pctBarColor((p.cpuUsageMillis / maxCpu) * 100)}`}
                            style={{ width: `${(p.cpuUsageMillis / maxCpu) * 100}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium flex items-center gap-2">
                    <HardDrive className="h-3.5 w-3.5 text-muted-foreground" />Top memory consumers
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {[...pods].sort((a, b) => b.memUsageBytes - a.memUsageBytes).slice(0, 5).map((p) => (
                    <div key={`${p.namespace}/${p.name}`} className="flex items-center gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-baseline justify-between mb-1">
                          <span className="text-xs font-mono truncate max-w-[180px]" title={p.name}>{p.name}</span>
                          <span className="text-xs text-muted-foreground ml-2 shrink-0">{fmtMiB(p.memUsageBytes)}</span>
                        </div>
                        <div className="relative h-1.5 w-full rounded-full bg-muted overflow-hidden">
                          <div className="absolute left-0 top-0 h-full rounded-full bg-info"
                            style={{ width: `${(p.memUsageBytes / maxMem) * 100}%` }} />
                        </div>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      )}

      {/* ── PODS ──────────────────────────────────────────────────────────────── */}
      {tab === "pods" && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Activity className="h-4 w-4" />
                Pods ({filteredPods.length}{nsFilter !== "__all__" || podSearch ? ` of ${pods.length}` : ""})
              </CardTitle>
              <div className="flex gap-2">
                <input type="text" placeholder="Search pods…" value={podSearch}
                  onChange={(e) => setPodSearch(e.target.value)}
                  className="h-8 rounded-md bg-background px-3 text-xs outline-none focus:ring-1 focus:ring-ring w-44" />
                <Select value={nsFilter} onValueChange={setNsFilter}>
                  <SelectTrigger className="h-8 text-xs w-40"><SelectValue placeholder="All namespaces" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All namespaces</SelectItem>
                    {podNamespaces.map((ns) => <SelectItem key={ns} value={ns}>{ns}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <SortableHead col="name" label="Name" />
                    <SortableHead col="namespace" label="Namespace" />
                    <SortableHead col="status" label="Status" />
                    <TableHead>Ready</TableHead>
                    <TableHead>Node</TableHead>
                    <SortableHead col="restarts" label="Restarts" />
                    <SortableHead col="cpu" label="CPU" />
                    <SortableHead col="mem" label="Memory" />
                    <TableHead>Age</TableHead>
                    <TableHead />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredPods.length === 0 ? (
                    <TableRow><TableCell colSpan={10} className="h-24 text-center text-muted-foreground text-sm">No pods found</TableCell></TableRow>
                  ) : filteredPods.map((pod) => {
                    const st = pod.status.toLowerCase();
                    const rowBg = st === "failed" || st.includes("crash") ? "bg-danger-bg/50" : st === "pending" ? "bg-warning-bg/50" : "";
                    return (
                      <TableRow key={`${pod.namespace}/${pod.name}`} className={rowBg}>
                        <TableCell className="font-mono text-xs max-w-[200px] truncate" title={pod.name}>{pod.name}</TableCell>
                        <TableCell><Badge variant="outline" className="text-2xs font-normal">{pod.namespace}</Badge></TableCell>
                        <TableCell><StatusBadge status={pod.status} /></TableCell>
                        <TableCell className="text-xs text-muted-foreground">{pod.ready}</TableCell>
                        <TableCell className="text-xs text-muted-foreground">{pod.node}</TableCell>
                        <TableCell>
                          <span className={`text-xs font-semibold tabular-nums ${pod.restarts >= 10 ? "text-destructive" : pod.restarts >= 3 ? "text-warning" : "text-muted-foreground"}`}>
                            {pod.restarts >= 3 ? "⚠ " : ""}{pod.restarts}
                          </span>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <span className="text-xs font-mono text-muted-foreground">{fmtMilliCPU(pod.cpuUsageMillis)}</span>
                            <MiniBar value={pod.cpuUsageMillis} max={maxCpu} color={pctBarColor((pod.cpuUsageMillis / maxCpu) * 100)} />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <span className="text-xs font-mono text-muted-foreground">{fmtMiB(pod.memUsageBytes)}</span>
                            <MiniBar value={pod.memUsageBytes} max={maxMem} color="bg-info" />
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{pod.age}</TableCell>
                        <TableCell>
                          <Button size="sm" variant="ghost" className="h-6 px-2 text-xs"
                            onClick={() => setLogPod({ name: pod.name, namespace: pod.namespace })}>
                            <FileText className="h-3 w-3 mr-1" />Logs
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── NAMESPACES ────────────────────────────────────────────────────────── */}
      {tab === "namespaces" && (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {namespaces.map((ns) => {
            const nsPods = pods.filter((p) => p.namespace === ns.name);
            const running = nsPods.filter((p) => p.status.toLowerCase() === "running").length;
            const bad = nsPods.filter((p) => !["running", "completed", "succeeded"].includes(p.status.toLowerCase())).length;
            return (
              <Card key={ns.name}
                className={`cursor-pointer hover:border-primary/40 transition-colors ${bad > 0 ? "border-warning/40" : ""}`}
                onClick={() => { setTab("pods"); setNsFilter(ns.name); }}>
                <CardContent className="pt-4 pb-4 space-y-3">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-mono text-sm font-semibold truncate">{ns.name}</p>
                    <StatusBadge status={ns.status} />
                  </div>
                  <div className="flex gap-4 text-xs">
                    <span className="text-muted-foreground">{nsPods.length} pods</span>
                    {running > 0 && <span className="text-success">{running} running</span>}
                    {bad > 0 && <span className="text-warning">{bad} unhealthy</span>}
                  </div>
                  {nsPods.length > 0 && (
                    <div className="flex h-1.5 w-full rounded-full overflow-hidden gap-0.5">
                      <div className="bg-success rounded-full" style={{ width: `${(running / nsPods.length) * 100}%` }} />
                      {bad > 0 && <div className="bg-warning rounded-full" style={{ width: `${(bad / nsPods.length) * 100}%` }} />}
                    </div>
                  )}
                  <p className="text-2xs text-muted-foreground">Created {ns.age} ago · click to view pods</p>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* ── SERVICES ──────────────────────────────────────────────────────────── */}
      {tab === "services" && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Network className="h-4 w-4" />Services ({filteredSvcs.length}{svcNsFilter !== "__all__" ? ` of ${services.length}` : ""})
              </CardTitle>
              <Select value={svcNsFilter} onValueChange={setSvcNsFilter}>
                <SelectTrigger className="h-8 text-xs w-40"><SelectValue placeholder="All namespaces" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__all__">All namespaces</SelectItem>
                  {svcNamespaces.map((ns) => <SelectItem key={ns} value={ns}>{ns}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Namespace</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Cluster IP</TableHead>
                    <TableHead>Ports</TableHead>
                    <TableHead>Selector</TableHead>
                    <TableHead>Age</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredSvcs.length === 0 ? (
                    <TableRow><TableCell colSpan={7} className="h-24 text-center text-muted-foreground text-sm">No services found</TableCell></TableRow>
                  ) : filteredSvcs.map((svc) => (
                    <TableRow key={`${svc.namespace}/${svc.name}`}>
                      <TableCell className="font-mono text-xs font-medium">{svc.name}</TableCell>
                      <TableCell><Badge variant="outline" className="text-2xs font-normal">{svc.namespace}</Badge></TableCell>
                      <TableCell><Badge variant="secondary" className="text-xs">{svc.type}</Badge></TableCell>
                      <TableCell className="font-mono text-xs text-muted-foreground">{svc.clusterIP}</TableCell>
                      <TableCell className="font-mono text-xs">{svc.ports || "—"}</TableCell>
                      <TableCell className="text-xs text-muted-foreground max-w-[200px] truncate">
                        {svc.selector && Object.keys(svc.selector).length > 0
                          ? Object.entries(svc.selector).map(([k, v]) => `${k}=${v}`).join(", ")
                          : "—"}
                      </TableCell>
                      <TableCell className="text-xs text-muted-foreground">{svc.age}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── EVENTS ────────────────────────────────────────────────────────────── */}
      {tab === "events" && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 text-warning" />Warning Events ({events.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {events.length === 0 ? (
              <div className="flex items-center gap-2 px-6 py-10 text-sm text-success">
                <CheckCircle2 className="h-4 w-4" />No warning events — cluster looks healthy.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Object</TableHead>
                      <TableHead>Namespace</TableHead>
                      <TableHead>Reason</TableHead>
                      <TableHead>Message</TableHead>
                      <TableHead>Count</TableHead>
                      <TableHead>Age</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {events.map((ev, i) => (
                      <TableRow key={i} className={ev.count >= 10 ? "bg-danger-bg/50" : "bg-warning-bg/50"}>
                        <TableCell className="font-mono text-xs max-w-[180px] truncate" title={ev.object}>{ev.object}</TableCell>
                        <TableCell><Badge variant="outline" className="text-2xs font-normal">{ev.namespace}</Badge></TableCell>
                        <TableCell>
                          <Badge variant="secondary" className="bg-warning-bg text-warning-fg border-transparent text-xs">{ev.reason}</Badge>
                        </TableCell>
                        <TableCell className="text-xs max-w-[300px] truncate" title={ev.message}>{ev.message}</TableCell>
                        <TableCell>
                          <span className={`text-xs font-semibold tabular-nums ${ev.count >= 10 ? "text-destructive" : "text-warning"}`}>{ev.count}</span>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">{ev.age}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* ── POD → SERVICE MAP ─────────────────────────────────────────────────── */}
      {tab === "pod-service-map" && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <CardTitle className="text-base flex items-center gap-2">
                <Server className="h-4 w-4" />Pod → Service Mapping ({filteredMap.length})
              </CardTitle>
              <div className="flex gap-2">
                <input type="text" placeholder="Search pods…" value={mapSearch}
                  onChange={(e) => setMapSearch(e.target.value)}
                  className="h-8 rounded-md bg-background px-3 text-xs outline-none focus:ring-1 focus:ring-ring w-40" />
                <Select value={mapNsFilter} onValueChange={setMapNsFilter}>
                  <SelectTrigger className="h-8 text-xs w-40"><SelectValue placeholder="All namespaces" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All namespaces</SelectItem>
                    {mapNamespaces.map((ns) => <SelectItem key={ns} value={ns}>{ns}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Pod</TableHead>
                    <TableHead>Namespace</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Backing Services</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMap.length === 0 ? (
                    <TableRow><TableCell colSpan={4} className="h-24 text-center text-muted-foreground text-sm">No entries found</TableCell></TableRow>
                  ) : filteredMap.map((entry) => (
                    <TableRow key={`${entry.namespace}/${entry.podName}`}>
                      <TableCell className="font-mono text-xs max-w-[220px] truncate" title={entry.podName}>{entry.podName}</TableCell>
                      <TableCell><Badge variant="outline" className="text-2xs font-normal">{entry.namespace}</Badge></TableCell>
                      <TableCell><StatusBadge status={entry.podStatus} /></TableCell>
                      <TableCell>
                        {entry.services.length === 0 ? (
                          <span className="text-xs text-muted-foreground">—</span>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {entry.services.map((svc) => (
                              <Badge key={svc} variant="secondary" className="text-xs font-mono">{svc}</Badge>
                            ))}
                          </div>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* ── ROUTING ───────────────────────────────────────────────────────────── */}
      {tab === "routing" && routing && (
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Network className="h-4 w-4" />Routing &amp; Subdomains
              </CardTitle>
              <div className="flex items-center gap-2">
                <span className="text-xs text-muted-foreground">Caddy proxy</span>
                {routing.caddy.reachable
                  ? <Badge variant="secondary" className="bg-success-bg text-success-fg border-transparent gap-1 text-xs"><CheckCircle2 className="h-3 w-3" />Up</Badge>
                  : <Badge variant="secondary" className="bg-danger-bg text-danger-fg border-transparent gap-1 text-xs"><XCircle className="h-3 w-3" />Down</Badge>}
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm font-medium">Platform Subdomains ({routing.platformSubdomains.length})</p>
                <input type="text" placeholder="Filter…" value={domainSearch}
                  onChange={(e) => setDomainSearch(e.target.value)}
                  className="h-7 rounded-md bg-background px-2 text-xs outline-none focus:ring-1 focus:ring-ring w-36" />
              </div>
              {routing.platformSubdomains.length === 0 ? (
                <p className="text-xs text-muted-foreground">No platform subdomains registered.</p>
              ) : (
                <div className="overflow-x-auto rounded-md bg-card">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Subdomain</TableHead>
                        <TableHead className="text-xs">Service</TableHead>
                        <TableHead className="text-xs">Project</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {routing.platformSubdomains
                        .filter((s: SystemPlatformSubdomain) =>
                          !domainSearch || s.subdomain.includes(domainSearch) ||
                          s.serviceName?.toLowerCase().includes(domainSearch.toLowerCase()) ||
                          s.projectName?.toLowerCase().includes(domainSearch.toLowerCase()))
                        .map((s: SystemPlatformSubdomain) => (
                          <TableRow key={s.subdomain}>
                            <TableCell className="font-mono text-xs max-w-[280px] truncate">{s.subdomain}</TableCell>
                            <TableCell className="text-xs">{s.serviceName || s.serviceId}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">{s.projectName || "—"}</TableCell>
                            <TableCell><Badge variant="secondary" className="text-xs capitalize">{s.serviceStatus || "—"}</Badge></TableCell>
                          </TableRow>
                        ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
            <div>
              <p className="text-sm font-medium mb-2">Custom Domains ({routing.customDomains.length})</p>
              {routing.customDomains.length === 0 ? (
                <p className="text-xs text-muted-foreground">No custom domains configured.</p>
              ) : (
                <div className="overflow-x-auto rounded-md bg-card">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="text-xs">Domain</TableHead>
                        <TableHead className="text-xs">Service</TableHead>
                        <TableHead className="text-xs">Status</TableHead>
                        <TableHead className="text-xs">TLS</TableHead>
                        <TableHead className="text-xs">Upstream</TableHead>
                        <TableHead className="text-xs">Last checked</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {routing.customDomains.map((d: SystemCustomDomain) => (
                        <TableRow key={d.domain}>
                          <TableCell className="font-mono text-xs">{d.domain}</TableCell>
                          <TableCell className="text-xs">{d.serviceName || d.serviceId || "—"}</TableCell>
                          <TableCell>
                            <Badge variant="secondary" className={`text-xs capitalize ${d.status === "active" ? "bg-success-bg text-success-fg border-transparent" : d.status === "error" ? "bg-danger-bg text-danger-fg border-transparent" : "bg-warning-bg text-warning-fg border-transparent"}`}>{d.status}</Badge>
                          </TableCell>
                          <TableCell className={`text-xs font-medium capitalize ${d.tlsStatus === "issued" ? "text-success" : d.tlsStatus === "error" ? "text-destructive" : "text-warning"}`}>{d.tlsStatus}</TableCell>
                          <TableCell className="font-mono text-xs text-muted-foreground">{d.nodeHost && d.nodePort ? `${d.nodeHost}:${d.nodePort}` : "—"}</TableCell>
                          <TableCell className="text-xs text-muted-foreground">{d.checkedAt ? new Date(d.checkedAt).toLocaleString() : "—"}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {logPod && <LogDrawer pod={logPod.name} namespace={logPod.namespace} onClose={() => setLogPod(null)} />}
    </div>
  );
}
